import { useState, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, ChevronRight, ChevronDown, Shield, Mail, Search, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from '@/components/ui/sonner';
import { useAccountUsers, useAddAccountUser } from '@/hooks/use-account-users';
import { withoutReadonlyAdmin } from '@/adapters/account-user.adapter';
import { MemberAliasesSection } from '@/modules/admin/components/member-aliases-section';
import { SentinelBrandsSection } from '@/modules/users/sentinel-brands-section';
import { useBusinessUnits } from '@/hooks/use-business-units';
import {
	canManageSentinelMembersAtom,
	useSentinelUserBrands,
} from '@/hooks/use-sentinel-routing';
import { useAtomValue } from 'jotai';
import { useAccounts } from '@/hooks/use-accounts';
import { useAuth, isSuperAdmin } from '@/hooks/use-auth';
import { usePermissions } from '@/hooks/use-permissions';
import { accountUsersService } from '@/services';
import type { AccountUser } from '@/types';

// ============================================================================
// Helpers
// ============================================================================

function getInitials(name: string) {
	return name
		.split(' ')
		.map((n) => n[0])
		.join('')
		.toUpperCase()
		.slice(0, 2);
}

// Role badge colours — agent: light blue, admin: light purple
const ROLE_BADGE: Record<string, { bg: string; text: string; label: string }> = {
	administrator:      { bg: 'bg-purple-100',  text: 'text-purple-600',  label: 'Admin' },
	agent:              { bg: 'bg-blue-100',    text: 'text-blue-600',    label: 'Agent' },
	ai:                 { bg: 'bg-emerald-100', text: 'text-emerald-600', label: 'AI' },
	light_agent:        { bg: 'bg-amber-100',   text: 'text-amber-600',   label: 'Light Agent' },
	client_restricted:  { bg: 'bg-slate-100',   text: 'text-slate-600',   label: 'Client Restricted' },
	sentinel:           { bg: 'bg-cyan-100',    text: 'text-cyan-700',    label: 'Sentinel' },
	readonly_admin:     { bg: 'bg-zinc-100',    text: 'text-zinc-600',    label: 'Read-only Admin' },
};

function RoleBadge({ role }: { role: string }) {
	const cfg = ROLE_BADGE[role];
	if (!cfg) return <span className='text-xs text-muted-foreground'>{role}</span>;
	return (
		<span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.text}`}>
			{cfg.label}
		</span>
	);
}

// ============================================================================
// Add User Modal — Figma nodes 2462-9229 (single) / 2462-14651 (bulk)
// ============================================================================

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const addUserSchema = z.object({
	name:  z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or fewer'),
	email: z.string().email('Invalid email address').max(254, 'Email is too long'),
	role:  z.string().min(1, 'Role is required'),
});

type AddUserFormValues = z.infer<typeof addUserSchema>;

interface AddUserModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

// Shared SelectContent styled to Figma node 1612-777
function RoleSelectContent() {
	return (
		<SelectContent className='w-[--radix-select-trigger-width] rounded-[16px] border border-gray-300 bg-white/80 backdrop-blur-md shadow-[0px_3px_12px_0px_rgba(0,0,0,0.08)] p-[6px]'>
			<SelectItem value='0' className='rounded-[10px] pl-[10px] pr-[6px] py-[4px] text-[13px] text-ink cursor-pointer focus:bg-gray-100 hover:bg-gray-100'>
				Agent
			</SelectItem>
			<SelectItem value='1' className='rounded-[10px] pl-[10px] pr-[6px] py-[4px] text-[13px] text-ink cursor-pointer focus:bg-gray-100 hover:bg-gray-100'>
				Administrator
			</SelectItem>
			<SelectItem value='4' className='rounded-[10px] pl-[10px] pr-[6px] py-[4px] text-[13px] text-ink cursor-pointer focus:bg-gray-100 hover:bg-gray-100'>
				Client Restricted User
			</SelectItem>
			<SelectItem value='5' className='rounded-[10px] pl-[10px] pr-[6px] py-[4px] text-[13px] text-ink cursor-pointer focus:bg-gray-100 hover:bg-gray-100'>
				Sentinel
			</SelectItem>
		</SelectContent>
	);
}

const selectTriggerCls = 'w-full h-[40px] rounded-[12px] border border-gray-500 bg-white text-[12px] pl-[12px] pr-[8px] placeholder:text-gray-800';

function AddUserModal({ open, onOpenChange }: AddUserModalProps) {
	const addUserMutation = useAddAccountUser();
	const [isBulk, setIsBulk]       = useState(false);
	const [bulkRole, setBulkRole]    = useState('');
	const [bulkEmails, setBulkEmails] = useState('');
	const [bulkError, setBulkError]  = useState('');

	const form = useForm<AddUserFormValues>({
		resolver: zodResolver(addUserSchema),
		defaultValues: { name: '', email: '', role: '' },
	});

	const handleOpenChange = (next: boolean) => {
		if (!next) {
			form.reset();
			setIsBulk(false);
			setBulkRole('');
			setBulkEmails('');
			setBulkError('');
		}
		onOpenChange(next);
	};

	const onSubmitSingle = async (values: AddUserFormValues) => {
		try {
			await addUserMutation.mutateAsync({ name: values.name, email: values.email, role: Number(values.role) });
			toast.success('Team member added successfully');
			handleOpenChange(false);
		} catch {
			toast.error('Failed to add user. Please try again.');
		}
	};

	const onSubmitBulk = async () => {
		setBulkError('');
		if (!bulkRole) { setBulkError('Please select a role.'); return; }
		const emails = bulkEmails.split(/[\n,]+/).map((e) => e.trim()).filter(Boolean);
		if (emails.length === 0) { setBulkError('Please enter at least one email address.'); return; }
		if (emails.length > 50) { setBulkError('Maximum 50 email addresses allowed.'); return; }
		const invalid = emails.filter((e) => !EMAIL_RE.test(e));
		if (invalid.length > 0) {
			setBulkError(`Invalid: ${invalid.slice(0, 2).join(', ')}${invalid.length > 2 ? '…' : ''}`);
			return;
		}
		try {
			await Promise.all(emails.map((email) =>
				addUserMutation.mutateAsync({ name: email, email, role: Number(bulkRole) }),
			));
			toast.success(`${emails.length} member${emails.length > 1 ? 's' : ''} invited`);
			handleOpenChange(false);
		} catch {
			toast.error('Failed to send invites. Please try again.');
		}
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent
				showCloseButton={false}
				className='p-0 gap-0 max-w-[440px] rounded-[20px] border border-gray-500 bg-white/80 backdrop-blur-md shadow-[0px_3px_12px_0px_rgba(0,0,0,0.08)] overflow-hidden'
			>
				{/* Header — title left-aligned */}
				<div className='flex h-[50px] items-center px-3 py-2 shrink-0'>
					<p className='text-[14px] font-semibold leading-5 text-ink'>Add team member</p>
				</div>

				<div className='h-px bg-gray-500 shrink-0' />

				{/* Content */}
				<div className='flex flex-col gap-6 p-4 shrink-0'>

					{/* Mode selector row + subtle divider */}
					<div className='flex flex-col gap-4'>
						<div className='flex items-center justify-between'>
							<span className='text-xs font-medium text-gray-900'>Choose a method</span>
							{/* Segmented pill tabs */}
							<div className='flex items-center bg-gray-100 rounded-full p-1'>
								<button
									type='button'
									onClick={() => setIsBulk(false)}
									className={[
										'px-3 py-1.5 rounded-full text-[13px] transition-all cursor-pointer',
										!isBulk
											? 'bg-white border border-gray-500 shadow-[0px_3px_6px_0px_rgba(0,0,0,0.08)] backdrop-blur-sm font-medium text-ink-strong'
											: 'font-normal text-gray-800',
									].join(' ')}
								>
									Single
								</button>
								<button
									type='button'
									onClick={() => setIsBulk(true)}
									className={[
										'px-3 py-[6px] rounded-full text-[13px] transition-all cursor-pointer',
										isBulk
											? 'bg-white border border-gray-500 shadow-[0px_3px_6px_0px_rgba(0,0,0,0.08)] backdrop-blur-sm font-medium text-ink-strong'
											: 'font-normal text-gray-800',
									].join(' ')}
								>
									Bulk
								</button>
							</div>
						</div>
						<div className='h-px bg-gray-500' />
					</div>

					{/* Form fields
					    min-h-[240px] locks the modal height: single mode fills it exactly
					    (3 fields × 64px + 2 gaps × 24px = 240px), bulk textarea gets flex-1
					    to fill the same space — no layout shift on mode switch. */}
					{!isBulk ? (
						<Form {...form}>
							<form
								id='add-user-form'
								onSubmit={form.handleSubmit(onSubmitSingle)}
								className='flex flex-col gap-6 min-h-60'
							>
								{/* Role */}
								<FormField
									control={form.control}
									name='role'
									render={({ field }) => (
										<FormItem className='gap-2'>
											<FormLabel className='text-[12px] font-medium text-gray-900'>Role</FormLabel>
											<Select onValueChange={field.onChange} value={field.value}>
												<FormControl>
													<SelectTrigger className={selectTriggerCls}>
														<SelectValue placeholder='Choose a role' />
													</SelectTrigger>
												</FormControl>
												<RoleSelectContent />
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>
								{/* Full Name */}
								<FormField
									control={form.control}
									name='name'
									render={({ field }) => (
										<FormItem className='gap-2'>
											<FormLabel className='text-[12px] font-medium text-gray-900'>Full Name</FormLabel>
											<FormControl>
												<Input
													placeholder='Enter your full name'
													{...field}
													className='h-10 rounded-[12px] border border-gray-500 bg-white text-[12px] placeholder:text-gray-800'
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								{/* Email Address */}
								<FormField
									control={form.control}
									name='email'
									render={({ field }) => (
										<FormItem className='gap-2'>
											<FormLabel className='text-[12px] font-medium text-gray-900'>Email Address</FormLabel>
											<FormControl>
												<Input
													type='email'
													placeholder='Enter your email address'
													{...field}
													className='h-10 rounded-[12px] border border-gray-500 bg-white text-[12px] placeholder:text-gray-800'
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</form>
						</Form>
					) : (
						<div className='flex flex-col gap-6 h-60'>
							{/* Role (bulk) */}
							<div className='flex flex-col gap-2'>
								<label className='text-[12px] font-medium text-gray-900'>Role</label>
								<Select onValueChange={setBulkRole} value={bulkRole}>
									<SelectTrigger className={selectTriggerCls}>
										<SelectValue placeholder='Choose a role for all the invitees' />
									</SelectTrigger>
									<RoleSelectContent />
								</Select>
							</div>
							{/* Email textarea — flex-1 fills remaining space to match single mode height */}
							<div className='flex flex-col gap-2 flex-1'>
								<label className='text-[12px] font-medium text-gray-900'>Email Address</label>
								<textarea
									value={bulkEmails}
									onChange={(e) => { setBulkEmails(e.target.value); setBulkError(''); }}
									placeholder='jane@company.com, alex@company.com, billy@company.com'
									className='flex-1 min-h-[80px] resize-none rounded-[16px] border border-gray-500 bg-white p-3 text-[12px] text-ink placeholder:text-gray-800 outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0'
								/>
								{bulkError
									? <p className='text-[11px] text-red-500'>{bulkError}</p>
									: <p className='text-[11px] text-gray-800'>Paste up to 50 email addresses, separated by commas or line breaks</p>
								}
							</div>
						</div>
					)}
				</div>

				{/* Footer */}
				<div className='flex items-center justify-end gap-1.5 px-3 pb-3 pt-0 shrink-0'>
					<button
						type='button'
						onClick={() => handleOpenChange(false)}
						className='h-8 px-4 rounded-full border border-gray-500 bg-white text-[13px] text-ink-strong hover:bg-gray-100 transition-colors cursor-pointer'
					>
						Cancel
					</button>
					<button
						type={!isBulk ? 'submit' : 'button'}
						form={!isBulk ? 'add-user-form' : undefined}
						onClick={isBulk ? onSubmitBulk : undefined}
						disabled={addUserMutation.isPending}
						className='h-8 px-4 rounded-full bg-ink-strong border border-ink-strong text-[13px] text-white hover:bg-ink transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2'
					>
						{addUserMutation.isPending ? (
							<>
								<span className='h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin' />
								{!isBulk ? 'Adding…' : 'Sending…'}
							</>
						) : !isBulk ? (
							<>
								<Plus className='h-3.5 w-3.5' />
								Add to team
							</>
						) : (
							'Send Invite'
						)}
					</button>
				</div>
			</DialogContent>
		</Dialog>
	);
}

// ============================================================================
// Users table — matches Figma design
// ============================================================================

type SortKey = 'name' | 'role' | 'lastUpdated';

function TeamMembersSection() {
	const { data: allUsers = [], isLoading, isError } = useAccountUsers();
	const users                               = useMemo(() => withoutReadonlyAdmin(allUsers), [allUsers]);
	const [sheetOpen, setSheetOpen]           = useState(false);
	const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
	const [search, setSearch]                 = useState('');
	const [sortKey, setSortKey]               = useState<SortKey>('name');
	const [sortDir, setSortDir]               = useState<'asc' | 'desc'>('asc');
	const { currentUser }                     = useAuth();
	const { can }                             = usePermissions();
	const canManageUsers                      = can('edit', 'access.users');
	// A Sentinel's brands and caps: administrators only.
	const canSetSentinelBrands                = useAtomValue(canManageSentinelMembersAtom);
	const sentinelBrandsByUser                = useSentinelUserBrands();
	const { businessUnits }                   = useBusinessUnits();
	const activeBrands = useMemo(
		() => businessUnits.filter((unit) => unit.status === 'active').map(({ id, name }) => ({ id, name })),
		[businessUnits],
	);

	const toggleSort = (key: SortKey) => {
		if (sortKey === key) {
			setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortKey(key);
			setSortDir('asc');
		}
	};

	const filtered = search.trim()
		? users.filter(
				(u) =>
					u.name.toLowerCase().includes(search.toLowerCase()) ||
					u.email.toLowerCase().includes(search.toLowerCase()),
			)
		: users;

	const sorted = [...filtered].sort((a, b) => {
		let av: string, bv: string;
		if (sortKey === 'name')      { av = a.name;      bv = b.name; }
		else if (sortKey === 'role') { av = a.role;      bv = b.role; }
		else                         { av = a.createdAt; bv = b.createdAt; }
		return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
	});

	return (
		<section className='flex flex-col h-full'>
			{/* ── Top bar: title + search + add member ── */}
			<div className='sticky top-0 backdrop-blur-sm bg-white/80 border-b border-border flex flex-col shrink-0 z-10'>
				<div className='flex h-[60px] items-center justify-between px-4'>
					<p className='text-base font-semibold text-foreground'>Users</p>
					<div className='flex items-center gap-3'>
						{/* Search pill — matches Figma: w-[280px], rounded-full, border */}
						<div className='relative flex items-center'>
							<Search className='absolute left-3 h-3.5 w-3.5 text-muted-foreground pointer-events-none' />
							<input
								type='text'
								placeholder='Search'
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className='h-8 w-[280px] rounded-full border border-border bg-white pl-8 pr-3 text-[11px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0'
							/>
						</div>
						{/* Add member button — dark pill */}
						{canManageUsers && (
							<button
								type='button'
								onClick={() => setSheetOpen(true)}
								className='cursor-pointer flex h-8 items-center gap-2 rounded-full bg-ink-strong border border-ink-strong px-4 text-[13px] text-white hover:bg-ink transition-colors'
							>
								<Plus className='h-3.5 w-3.5' />
								Add member
							</button>
						)}
					</div>
				</div>

				{/* ── Column header row ── */}
				<div className='border-t border-border flex items-stretch px-4'>
					<div className='flex flex-1 items-center justify-between pr-3 py-2 min-w-0'>
						<span className='text-xs font-medium text-muted-foreground'>Name</span>
						<button type='button' onClick={() => toggleSort('name')} className='cursor-pointer p-0.5 hover:text-foreground transition-colors'>
							<ChevronsUpDown className='h-3.5 w-3.5 text-muted-foreground' />
						</button>
					</div>
					<div className='w-px h-4 my-auto bg-border shrink-0' />
					<div className='flex flex-1 items-center justify-between px-3 py-2 min-w-0'>
						<span className='text-xs font-medium text-muted-foreground'>Role</span>
						<button type='button' onClick={() => toggleSort('role')} className='cursor-pointer p-0.5 hover:text-foreground transition-colors'>
							<ChevronsUpDown className='h-3.5 w-3.5 text-muted-foreground' />
						</button>
					</div>
					<div className='w-px h-4 my-auto bg-border shrink-0' />
					<div className='flex flex-1 items-center justify-between px-3 py-2 min-w-0'>
						<span className='text-xs font-medium text-muted-foreground'>Last Updated</span>
						<button type='button' onClick={() => toggleSort('lastUpdated')} className='cursor-pointer p-0.5 hover:text-foreground transition-colors'>
							<ChevronsUpDown className='h-3.5 w-3.5 text-muted-foreground' />
						</button>
					</div>
				</div>
			</div>

			<AddUserModal open={sheetOpen} onOpenChange={setSheetOpen} />

			{/* ── Table body ── */}
			<div className='flex flex-col px-4 py-2'>
				{isError ? (
					<div className='rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive mt-2'>
						Failed to load team members. Please try again later.
					</div>
				) : isLoading ? (
					<>
						{[1, 2, 3].map((i) => (
							<div key={i} className='flex items-center h-12 gap-4 px-0'>
								<div className='flex flex-1 items-center gap-3 min-w-0'>
									<Skeleton className='h-8 w-8 rounded-full shrink-0' />
									<div className='space-y-1 flex-1 min-w-0'>
										<Skeleton className='h-3 w-32' />
										<Skeleton className='h-3 w-48' />
									</div>
								</div>
								<div className='flex-1 min-w-0'>
									<Skeleton className='h-5 w-14 rounded-full' />
								</div>
								<div className='flex-1 min-w-0'>
									<Skeleton className='h-3 w-24' />
								</div>
							</div>
						))}
					</>
				) : sorted.length === 0 && search.trim() ? (
					<div className='py-10 text-center text-sm text-muted-foreground'>
						No members match &ldquo;{search}&rdquo;
					</div>
				) : users.length === 0 ? (
					<div className='py-10 text-center text-sm text-muted-foreground'>
						No team members yet. Add your first member to get started.
					</div>
				) : (
					sorted.map((user, idx) => {
						const isExpanded          = expandedUserId === user.id;
						const isSelf              = currentUser?.id != null && String(currentUser.id) === String(user.id);
						const canManageThisAliases = canManageUsers || isSelf;
						const aliasAccountUserId  = user.accountUserId;
						const aliasToggleAllowed  = typeof aliasAccountUserId === 'number';
						const showAliases         = aliasToggleAllowed && canManageThisAliases;
						const showBrands          = user.role === 'sentinel' && canSetSentinelBrands;

						return (
							<div key={user.id}>
								{/* Divider between rows (skip first) */}
								{idx > 0 && <div className='h-px bg-border mx-0' />}

								<div className='flex items-stretch h-12 rounded-[10px] hover:bg-gray-100 transition-colors cursor-default group'>
									{/* Name column */}
									<div className='flex flex-1 items-center gap-3 pl-0 pr-3 min-w-0'>
										<Avatar className='h-8 w-8 shrink-0'>
											<AvatarFallback className='text-xs font-medium bg-muted'>
												{getInitials(user.name)}
											</AvatarFallback>
										</Avatar>
										<div className='flex flex-col min-w-0'>
											<span className='text-[13px] font-normal text-foreground truncate leading-[18px]'>{user.name}</span>
											<span className='text-[11px] text-muted-foreground truncate leading-[16px] flex items-center gap-1'>
												<Mail className='h-3 w-3 shrink-0' />
												{user.email}
											</span>
										</div>
									</div>

									<div className='w-px self-stretch h-4 my-auto bg-border shrink-0' />

									{/* Role column */}
									<div className='flex flex-1 items-center gap-2 px-3 min-w-0'>
										<RoleBadge role={user.role} />
									</div>

									<div className='w-px self-stretch h-4 my-auto bg-border shrink-0' />

									{/* Last Updated column */}
									<div className='flex flex-1 items-center gap-3 px-3 min-w-0'>
										<span className='text-xs text-gray-800'>
											{new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
										</span>
										{/* Row dropdown: a Sentinel's brands and the aliases, when permitted */}
										{(showAliases || showBrands) && (
											<Button
												variant='ghost'
												size='sm'
												className='h-6 w-6 p-0 ml-auto opacity-0 group-hover:opacity-100 transition-opacity'
												onClick={() => setExpandedUserId(isExpanded ? null : user.id)}
												aria-label={isExpanded ? 'Collapse details' : 'Show details'}
											>
												<ChevronDown
													className='h-3.5 w-3.5 transition-transform'
													style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
												/>
											</Button>
										)}
									</div>
								</div>

								{isExpanded && showBrands && (
									<SentinelBrandsSection
										userId={user.id}
										name={user.name}
										saved={sentinelBrandsByUser[String(user.id)] ?? []}
										brands={activeBrands}
									/>
								)}

								{/* Aliases expansion (preserved functionality) */}
								{isExpanded && showAliases && aliasAccountUserId != null && (
									<MemberAliasesSection
										accountUserId={aliasAccountUserId}
										canManage={canManageThisAliases}
									/>
								)}
							</div>
						);
					})
				)}
			</div>
		</section>
	);
}

// ============================================================================
// Collapsible Account Row (Super Admin) — preserved in full
// ============================================================================

interface AccountRowProps {
	account: { id: string; name: string; sub_domain?: string | null };
}

function AccountRow({ account }: AccountRowProps) {
	const [isOpen, setIsOpen]             = useState(false);
	const [users, setUsers]               = useState<AccountUser[]>([]);
	const [isLoadingUsers, setIsLoadingUsers] = useState(false);
	const [hasLoaded, setHasLoaded]       = useState(false);

	const handleToggle = useCallback(
		async (open: boolean) => {
			setIsOpen(open);
			if (open && !hasLoaded) {
				setIsLoadingUsers(true);
				try {
					const result = await accountUsersService.getAccountUsersForAccount(account.id);
					setUsers(withoutReadonlyAdmin(result));
					setHasLoaded(true);
				} catch (err) {
					const status  = (err as { response?: { status?: number } })?.response?.status;
					const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '';
					if (status === 403 && message.includes('another account')) {
						toast.error('You can only view users in your own account.');
					} else {
						toast.error(`Failed to load users for ${account.name}`);
					}
				} finally {
					setIsLoadingUsers(false);
				}
			}
		},
		[account.id, account.name, hasLoaded],
	);

	return (
		<Collapsible open={isOpen} onOpenChange={handleToggle}>
			<CollapsibleTrigger asChild>
				<div className='flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors'>
					<ChevronRight
						className='h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200'
						style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
					/>
					<div className='flex h-7 w-7 items-center justify-center rounded bg-muted text-xs font-semibold shrink-0'>
						{account.name.slice(0, 2).toUpperCase()}
					</div>
					<div className='flex-1 min-w-0'>
						<p className='text-sm font-medium truncate'>{account.name}</p>
						{account.sub_domain && <p className='text-xs text-muted-foreground truncate'>{account.sub_domain}</p>}
					</div>
					<span className='text-xs text-muted-foreground border border-border rounded-full px-2 py-0.5 shrink-0'>
						{isOpen ? 'Collapse' : 'View users'}
					</span>
				</div>
			</CollapsibleTrigger>

			<CollapsibleContent>
				<div className='bg-muted/30 border-t'>
					{isLoadingUsers ? (
						<div className='space-y-2 px-4 py-3'>
							{[1, 2].map((i) => (
								<div key={i} className='flex items-center gap-3'>
									<Skeleton className='h-7 w-7 rounded-full' />
									<Skeleton className='h-3 flex-1' />
									<Skeleton className='h-5 w-16 rounded-full' />
								</div>
							))}
						</div>
					) : users.length === 0 ? (
						<p className='px-12 py-3 text-xs text-muted-foreground'>No users in this account.</p>
					) : (
						users.map((user) => (
							<div key={user.id} className='flex items-center gap-3 px-12 py-2.5 border-t first:border-t-0'>
								<Avatar className='h-7 w-7 shrink-0'>
									<AvatarFallback className='text-[10px] font-medium bg-background'>{getInitials(user.name)}</AvatarFallback>
								</Avatar>
								<div className='flex-1 min-w-0'>
									<p className='text-xs font-medium truncate'>{user.name}</p>
									<p className='text-xs text-muted-foreground truncate'>{user.email}</p>
								</div>
								<RoleBadge role={user.role} />
							</div>
						))
					)}
				</div>
			</CollapsibleContent>
		</Collapsible>
	);
}

// ============================================================================
// Super Admin Section — preserved in full
// ============================================================================

function SuperAdminSection() {
	const { data, isLoading, isError } = useAccounts();
	const accounts = data?.accounts ?? [];

	return (
		<section>
			<div className='flex items-center gap-2.5 mb-4 px-4'>
				<div className='flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100'>
					<Shield className='h-4 w-4 text-violet-600' />
				</div>
				<div>
					<h2 className='text-sm font-semibold leading-none'>All Accounts</h2>
					<p className='text-xs text-muted-foreground mt-0.5'>Super admin view — expand to manage users</p>
				</div>
			</div>

			{isError ? (
				<div className='mx-4 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive'>
					Failed to load accounts. Please try again later.
				</div>
			) : isLoading ? (
				<div className='rounded-lg border divide-y mx-4'>
					{[1, 2, 3].map((i) => (
						<div key={i} className='flex items-center gap-3 px-4 py-3'>
							<Skeleton className='h-7 w-7 rounded' />
							<div className='flex-1 space-y-1.5'>
								<Skeleton className='h-3 w-36' />
								<Skeleton className='h-3 w-24' />
							</div>
						</div>
					))}
				</div>
			) : accounts.length === 0 ? (
				<p className='px-4 text-sm text-muted-foreground'>No accounts found.</p>
			) : (
				<div className='rounded-lg border divide-y mx-4'>
					{accounts.map((account) => (
						<AccountRow key={account.id} account={account} />
					))}
				</div>
			)}
		</section>
	);
}

// ============================================================================
// Users Page
// ============================================================================

export default function UsersPage() {
	const { currentUser } = useAuth();
	const showSuperAdmin  = isSuperAdmin(currentUser);

	return (
		<div className='flex flex-col h-full'>
			<TeamMembersSection />

			{showSuperAdmin && (
				<>
					<Separator className='my-6' />
					<SuperAdminSection />
				</>
			)}
		</div>
	);
}
